import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Await,
    FetcherWithComponents,
    useAsyncValue,
    useFetcher,
    useLoaderData,
} from "react-router-dom";
import { formatDistance } from "date-fns";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { useSnackbar } from "notistack";
import { useTheme } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Backdrop from "@mui/material/Backdrop";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";
import IconButton from "@mui/material/IconButton";
import PersonIcon from "@mui/icons-material/Person";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import GroupIcon from "@mui/icons-material/Group";

import {
    GetAddressBookDetailsResponse,
    GetContactsResponseItem,
} from "services/apiService";
import PhoneNumberInput from "components/PhoneNumberInput";

export enum AddressBookDetailsFormAction {
    DeleteContacts = "DELETE_CONTACTS",
    AddContact = "ADD_CONTACTS",
}

interface CreateContactFormProps {
    open: boolean;
    onClose: () => void;
    fetcher: FetcherWithComponents<any>;
}

const ContactListitem = ({
    contact,
    checked,
    handleCheckedToggle,
}: {
    contact: GetContactsResponseItem;
    checked: boolean;
    handleCheckedToggle: (contactId: string) => void;
}) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const fetcher = useFetcher();
    const { enqueueSnackbar } = useSnackbar();
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const [isHovering, setIsHovering] = useState(false);
    const handleMouseOver = () => {
        setIsHovering(true);
    };
    const handleMouseOut = () => {
        setIsHovering(false);
    };

    useEffect(() => {
        if (fetcher.data) {
            if (fetcher.data.success === true) {
                switch (fetcher.data.action) {
                    case AddressBookDetailsFormAction.DeleteContacts:
                        enqueueSnackbar("Contacts deleted successfully!", {
                            variant: "success",
                        });
                        break;

                    default:
                        break;
                }
            } else {
                enqueueSnackbar(fetcher.data.error, { variant: "error" });
            }

        }
    }, [fetcher.data, enqueueSnackbar]);

    return (
        <>
            <ListItem
                disablePadding
                component={Paper}
                variant="outlined"
                secondaryAction={
                    <IconButton aria-label="more" onClick={handleClick}>
                        <MoreHorizIcon fontSize="inherit" />
                    </IconButton>
                }
            >
                <ListItemButton
                    onMouseOver={handleMouseOver}
                    onMouseOut={handleMouseOut}
                    onClick={() => handleCheckedToggle(contact.id)}
                >
                    <ListItemIcon>
                        {isHovering || checked ? (
                            <Checkbox
                                edge="start"
                                checked={checked}
                                tabIndex={-1}
                                disableRipple
                                inputProps={{ "aria-labelledby": contact.id }}
                            />
                        ) : (
                            <PersonIcon style={{ color: '#226CE6 ' }} />
                        )}
                    </ListItemIcon>

                    <ListItemText
                        primary={
                            <Typography variant="inherit" noWrap>
                                {contact.phoneNumber}
                            </Typography>
                        }
                        secondary={
                            <Typography variant="inherit" noWrap>
                                {formatDistance(new Date(contact.createdOnUtc), new Date(), {
                                    addSuffix: true,
                                })}
                            </Typography>
                        }
                    />
                </ListItemButton>
            </ListItem>

            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                {/* <MenuItem onClick={handleClose}>
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem> */}
                <MenuItem>
                    <fetcher.Form method="delete" id="deleteAddressBookForm">
                        <LoadingButton
                            aria-label="delete"
                            type="submit"
                            form="deleteAddressBookForm"
                            startIcon={<DeleteIcon />}
                            loading={fetcher.state === "submitting"}
                            color="error"
                        >
                            Delete
                        </LoadingButton>
                        <input
                            type="hidden"
                            name="action"
                            value={AddressBookDetailsFormAction.DeleteContacts}
                        />
                        <input
                            type="hidden"
                            name="contact_ids"
                            value={contact.id}
                        />
                    </fetcher.Form>
                </MenuItem>
            </Menu>
        </>
    );
};

const buildContactRowRenderer = ({
    checkedContactIds,
    handleCheckedToggle,
}: {
    checkedContactIds: string[];
    handleCheckedToggle: (value: string) => void;
}) => {
    return ({
        data,
        index,
        style,
    }: ListChildComponentProps<GetContactsResponseItem[]>) => {
        const contact = data[index];

        return (
            <div key={contact.id} style={style}>
                <ContactListitem
                    contact={contact}
                    checked={checkedContactIds.indexOf(contact.id) !== -1}
                    handleCheckedToggle={handleCheckedToggle}
                />
            </div>
        );
    };
};

const ContactList = ({
    contacts,
    checkedContactIds,
    handleCheckedToggle,
}: {
    contacts: GetContactsResponseItem[];
    checkedContactIds: string[];
    handleCheckedToggle: (contactId: string) => void;
}) => {
    const renderContactRow = useMemo(
        () =>
            buildContactRowRenderer({
                checkedContactIds,
                handleCheckedToggle: handleCheckedToggle,
            }),
        [checkedContactIds, handleCheckedToggle]
    );

    return contacts.length > 0 ? (
        <div style={{ display: "flex", flexGrow: 1 }} className="contact-list">
            <div style={{ flex: "1 1 auto" }}>
                <AutoSizer>
                    {({ height, width }) => (
                        <FixedSizeList
                            height={height}
                            width={width}
                            itemSize={88}
                            itemCount={contacts.length}
                            itemData={contacts}
                        >
                            {renderContactRow}
                        </FixedSizeList>
                    )}
                </AutoSizer>
            </div>
        </div>
    ) : (
        <div>No contacts</div>
    );
};







const CreateContactForm = ({
    open,
    onClose,
    fetcher,
}: CreateContactFormProps) => {
    const theme = useTheme();

    // Close dialog when fetcher data changes
    useEffect(() => {
        onClose();
    }, [fetcher.data, onClose]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>New contact</DialogTitle>

            <DialogContent>
                <fetcher.Form
                    method="post"
                    id="createContactForm"
                    style={{
                        marginTop: theme.spacing(1),
                        marginBottom: theme.spacing(1),
                    }}
                >
                    <input
                        type="hidden"
                        name="action"
                        value={AddressBookDetailsFormAction.AddContact}
                    />

                    <PhoneNumberInput name="phone_number" required />
                </fetcher.Form>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>

                <LoadingButton
                    type="submit"
                    form="createContactForm"
                    loading={fetcher.state === "submitting"}
                >
                    Create
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

const AddressBookDetailsBox = () => {
    const addressBookDetails = useAsyncValue() as GetAddressBookDetailsResponse;

    // Original contacts
    const contacts = useMemo(
        () => addressBookDetails.list_contact,
        [addressBookDetails]
    );

    console.log("** contacts : ", contacts)

    // Filter contact list by search text
    const [searchText, setSearchText] = useState("");
    const filteredContacts = useMemo(() => {
        if (searchText.length === 0) return contacts;

        return contacts.filter((contact) =>
            contact.phoneNumber
                .toLocaleLowerCase()
                .includes(searchText.toLocaleLowerCase())
        );
    }, [contacts, searchText]);

    // Contact create form dialog handles
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const onCreateDialogClose = useCallback(() => {
        setIsCreateDialogOpen(false);
    }, [setIsCreateDialogOpen]);

    // Show success snackbars on contact added successfully
    const fetcher = useFetcher();
    const { enqueueSnackbar } = useSnackbar();
    useEffect(() => {
        if (fetcher.data) {
            if (fetcher.data.success === true) {
                switch (fetcher.data.action) {
                    case AddressBookDetailsFormAction.AddContact:
                        enqueueSnackbar("Contact added successfully!", {
                            variant: "success",
                        });
                        break;

                    case AddressBookDetailsFormAction.DeleteContacts:
                        enqueueSnackbar("Contacts deleted successfully!", {
                            variant: "success",
                        });
                        break;

                    default:
                        break;
                }
            } else {
                enqueueSnackbar(fetcher.data.error, { variant: "error" });
            }
            setCheckedContactIds([]);
        }
    }, [fetcher.data, enqueueSnackbar]);

    // Allow contacts multi-selection
    const [checkedContactIds, setCheckedContactIds] = useState<string[]>([]);
    const handleCheckedToggle = useCallback(
        (contactId: string) => {
            const currentIndex = checkedContactIds.indexOf(contactId);
            const newChecked = [...checkedContactIds];

            if (currentIndex === -1) {
                newChecked.push(contactId);
            } else {
                newChecked.splice(currentIndex, 1);
            }

            setCheckedContactIds(newChecked);
        },
        [checkedContactIds, setCheckedContactIds]
    );

    return (
        <>
            <Stack
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
                spacing={4}
            >
                <Typography variant="h4">
                    <span style={{ color: "grey" }}>{"Groups > "}</span>
                    <span>{addressBookDetails.details.name}</span>
                </Typography>

                <Box>
                    <GroupIcon sx={{ mr: 0.5 }} />
                    <span>{addressBookDetails.details.contactsAmount}</span>
                </Box>

                <Grid container alignItems="center" rowSpacing={2}>
                    <Grid item xs={12} md={8}>
                        <TextField
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                            placeholder="Search"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12} md={4} display="flex" justifyContent="end">
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setIsCreateDialogOpen(true)}
                        >
                            Add
                        </Button>
                    </Grid>
                </Grid>

                <ContactList
                    contacts={filteredContacts}
                    checkedContactIds={checkedContactIds}
                    handleCheckedToggle={handleCheckedToggle}
                />

                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <div>{checkedContactIds.length} selected</div>

                    <Button
                        variant="text"
                        sx={{ mx: 2 }}
                        onClick={() => setCheckedContactIds([])}
                    >
                        Unselect all
                    </Button>

                    <Box
                        sx={{
                            flexGrow: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "end",
                        }}
                    >
                        <fetcher.Form method="delete">
                            <input
                                type="hidden"
                                name="action"
                                value={AddressBookDetailsFormAction.DeleteContacts}
                            />

                            <input
                                type="hidden"
                                name="contact_ids"
                                value={checkedContactIds.join(",")}
                            />

                            <IconButton
                                disabled={checkedContactIds.length === 0}
                                aria-label="delete"
                                type="submit"
                            >
                                <DeleteIcon />
                            </IconButton>
                        </fetcher.Form>
                    </Box>
                </Box>

                <CreateContactForm
                    open={isCreateDialogOpen}
                    onClose={onCreateDialogClose}
                    fetcher={fetcher}
                />
            </Stack>

            <Backdrop
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={fetcher.state === "submitting"}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <CircularProgress color="inherit" />
                </div>
            </Backdrop>
        </>
    );
};

const AddressBookDetails = () => {
    const loaderData = useLoaderData() as {
        addressBookDetails: Promise<GetAddressBookDetailsResponse>;
    };

    return (
        <React.Suspense
            fallback={
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <CircularProgress />
                    <p>Fetching address book details...</p>
                </Box>
            }
        >
            <Await
                resolve={loaderData.addressBookDetails}
                errorElement={<p>Error while loading address book details.</p>}
            >
                <AddressBookDetailsBox />
            </Await>
        </React.Suspense>
    );
};

export default AddressBookDetails;
